import { toCanvas } from 'html-to-image';

const MAX_EDGE = 32760;
const MAX_PIXELS = 16_000_000;

/** 导出失败类型供页面翻译；不在用户提示中泄露资源地址或底层错误。 */
export class ConversationExportError extends Error {
  /** kind区分尺寸预算和资源失败，页面统一映射本地化提示。 */
  constructor(public readonly kind: 'size' | 'resource') { super(`Conversation export: ${kind}`); }
}

/** 按物理像素约束长图；只降低像素密度，不缩放布局或截断内容。 */
export function exportPixelRatio(width: number, height: number, deviceRatio: number): number {
  if (![width, height].every(value => Number.isFinite(value) && value > 0)
    || width > MAX_EDGE || height > MAX_EDGE || width * height > MAX_PIXELS) {
    throw new ConversationExportError('size');
  }
  // 向下取整密度，给canvas整数尺寸留余量；最少保留每CSS像素一个物理像素。
  const ratio = Math.min(2, Math.max(1, Number.isFinite(deviceRatio) ? deviceRatio : 1),
    MAX_EDGE / width, MAX_EDGE / height, Math.sqrt(MAX_PIXELS / (width * height)));
  return Math.max(1, Math.floor(ratio * 100) / 100);
}

/** 给字体、图片及渲染等待添加取消屏障；超时后不得继续下载旧账户的内容。 */
function abortable<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason);
    if (signal.aborted) { reject(signal.reason); return; }
    signal.addEventListener('abort', abort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

/** 图片先内嵌到快照再渲染，避免html-to-image把CORS/加载失败静默替换为空白。 */
async function embedSnapshotImage(image: HTMLImageElement, signal: AbortSignal): Promise<void> {
  const source = image.currentSrc || image.src;
  if (!source) throw new ConversationExportError('resource');
  const response = await fetch(source, { signal, credentials: 'same-origin' });
  if (!response.ok) throw new ConversationExportError('resource');
  const blob = await response.blob();
  const data = await abortable(new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new ConversationExportError('resource'));
    reader.readAsDataURL(blob);
  }), signal);
  image.removeAttribute('srcset'); image.removeAttribute('sizes');
  image.loading = 'eager'; image.src = data;
  await abortable(image.decode(), signal);
}

/** 找到真实不透明背景；消息滚动容器本身常为透明，不能导出成黑底。 */
function exportBackground(node: HTMLElement): string {
  for (let current: HTMLElement | null = node; current; current = current.parentElement) {
    const color = getComputedStyle(current).backgroundColor;
    if (color && color !== 'transparent' && color !== 'rgba(0, 0, 0, 0)') return color;
  }
  return '#ffffff';
}

/**
 * 生成公开对话PNG。先冻结DOM并过滤思考，再等待资源、测量全部消息边界。
 * 不滚动/改写真实页面；signal用于换会话、换账号或卸载时取消，所有路径清理离屏节点。
 */
export async function createConversationImage(source: HTMLElement, signal: AbortSignal): Promise<string> {
  signal.throwIfAborted();
  const controller = new AbortController();
  const cancel = () => controller.abort(signal.reason);
  signal.addEventListener('abort', cancel, { once: true });
  const timeout = window.setTimeout(() => controller.abort(new ConversationExportError('resource')), 20_000);
  const scope = controller.signal;
  const stage = document.createElement('div');
  stage.dataset.conversationExport = 'true'; stage.setAttribute('aria-hidden', 'true'); stage.inert = true;
  const width = Math.ceil(source.clientWidth);
  const background = exportBackground(source);
  Object.assign(stage.style, { position: 'fixed', top: '0', left: '0', transform: 'translateX(-100000px)', width: `${width}px`,
    pointerEvents: 'none', overflow: 'hidden', backgroundColor: background });
  try {
    const snapshot = source.cloneNode(true) as HTMLElement;
    snapshot.querySelectorAll('[data-thinking-block], [data-export-exclude], .katex-mathml, .sr-only').forEach(node => node.remove());
    Object.assign(snapshot.style, { height: 'auto', minHeight: '0', maxHeight: 'none', overflow: 'visible',
      width: `${width}px`, flex: 'none', transition: 'none', animation: 'none' });
    // cloneNode不复制canvas位图；保留公开画布，隐藏思考画布不参与导出。
    // 用transform离屏，避免CSS逻辑inset在html-to-image克隆时覆盖left，造成空白图。
    const originals = [...source.querySelectorAll('canvas')].filter(node => !node.closest('[data-thinking-block], [data-export-exclude]'));
    snapshot.querySelectorAll('canvas').forEach((canvas, index) => {
      const original = originals[index];
      canvas.width = original.width; canvas.height = original.height;
      const context = canvas.getContext('2d');
      if (!context) throw new ConversationExportError('resource');
      context.drawImage(original, 0, 0);
    });
    stage.append(snapshot); (source.parentElement ?? document.body).append(stage);
    await abortable(document.fonts.ready, scope);
    await Promise.all([...snapshot.querySelectorAll('img')].map(image => embedSnapshotImage(image, scope)));
    scope.throwIfAborted();
    const anchors = [...snapshot.querySelectorAll<HTMLElement>('[data-export-role]')]
      .map(node => node.getBoundingClientRect()).filter(rect => rect.height > 0);
    if (!anchors.length) throw new ConversationExportError('size');
    const origin = snapshot.getBoundingClientRect().top;
    const start = Math.floor(Math.min(...anchors.map(rect => rect.top)) - origin);
    const end = Math.ceil(Math.max(...anchors.map(rect => rect.bottom)) - origin);
    const height = end - start;
    const pixelRatio = exportPixelRatio(width, height, window.devicePixelRatio);
    stage.style.height = `${height}px`; snapshot.style.transform = `translateY(${-start}px)`;
    const canvas = await abortable(toCanvas(stage, { width, height, pixelRatio, backgroundColor: background,
      skipAutoScale: true, includeQueryParams: true, fetchRequestInit: { signal: scope },
      style: { transform: 'none', position: 'relative' } }), scope);
    scope.throwIfAborted();
    const png = canvas.toDataURL('image/png');
    if (!png.startsWith('data:image/png;base64,')) throw new ConversationExportError('size');
    return png.slice('data:image/png;base64,'.length);
  } catch (error) {
    if (scope.aborted) throw scope.reason;
    if (error instanceof ConversationExportError) throw error;
    throw new ConversationExportError('resource');
  } finally {
    stage.remove(); window.clearTimeout(timeout); signal.removeEventListener('abort', cancel);
    controller.abort();
  }
}
